import { useReactFlow } from '@xyflow/react'
import type { Node } from '@xyflow/react'
import type { ReactFlowNodeData } from '../../types/mindmap-components'
import { useFeatureAccessContext } from '../../context/featureAccess'
import { calculateNodeQualityScore, type QualityScoreResult } from './scoreCalculator'
import { NODE_QUALITY_CONFIG } from './config'

const SCOREABLE_CATEGORIES = new Set(Object.keys(NODE_QUALITY_CONFIG))

/**
 * Returns the quality score result for a node by its React Flow ID.
 * Returns null for node types not in NODE_QUALITY_CONFIG (non-generatable types),
 * or when the node_quality_gate feature toggle is disabled.
 *
 * Uses getNode() snapshot — same pattern as BaseNode's canGenerate check.
 * Re-renders naturally with the parent node component when node data changes.
 */
export function useNodeQualityScore(nodeId: string): QualityScoreResult | null {
  const { getNode } = useReactFlow()
  const { isFeatureEnabled } = useFeatureAccessContext()
  const node = getNode(nodeId) as Node<ReactFlowNodeData> | undefined

  // Hide chip entirely when quality gate is disabled
  if (!isFeatureEnabled('node_quality_gate')) return null

  if (!node) return null
  if (!SCOREABLE_CATEGORIES.has(node.data.category as string)) return null

  // Hide chip on AI-generated nodes unless the user has manually edited content
  if (node.data.aiGenerated && !node.data.userModified) return null

  return calculateNodeQualityScore(node.data)
}
